// src/Views/Header.js

import { useState } from "react";
import corporateThumbnail from "../assets/images/corporate-thumbnail.png";
import docThumbnail from "../assets/images/doc-thumbnail.png";
import dramaThumbnail from "../assets/images/drama-thumbnail.png";
import brandLogo from "../assets/images/logo-elya.png";
import desktopBackground from "../assets/images/mobile-still.png";
import narrativeBackground from "../assets/images/there-moonlight-still.png";
import websiteBg from "../assets/videos/website-bg-3.mp4";
import { BioCommercial, BioHeadshot, BioNarrative } from "../components/Bio";
import IconWrapper from "../components/IconWrapper";
import VideoModal from "../components/VideoModal";
import socialLinks from "../config/socialLinks";

import "./Header.css";

const services = [
  {
    id: "corporate",
    title: "CORPORATE",
    description: "Professional Portrait",
    image: corporateThumbnail,
  },
  {
    id: "doc",
    title: "DOCUMENTARY",
    description: "Social Issues | Human Interest",
    image: docThumbnail,
  },
  {
    id: "drama",
    title: "DRAMA REEL",
    description: "Performance-Driven Narrative",
    image: dramaThumbnail,
  },
];

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("commercial");
  const [cvError, setCvError] = useState(null);
  const [showContactDetails, setShowContactDetails] = useState(false);
  const [copiedItem, setCopiedItem] = useState(null);
  const [isClosing, setIsClosing] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showBio, setShowBio] = useState(false);
  const [bioActive, setBioActive] = useState(false);
  const [videoId, setVideoId] = useState(null);

  const handleTabChange = (tab) => {
    if (tab === activeTab) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setActiveTab(tab);
      setIsTransitioning(false);
    }, 300);
  };
  const openInNewTab = (url) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };
  const handleCvDownload = async () => {
    setCvError(null);

    try {
      const link = document.createElement("a");
      link.href = "/CV.pdf";
      link.target = "_blank";
      link.rel = "noopener noreferrer";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error opening CV:", error);
      setCvError("Failed to open CV. Please try again later.");
    }
  };

  const handleCopyToClipboard = (text, itemId) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopiedItem(itemId);
        setTimeout(() => setCopiedItem(null), 2000);
      })
      .catch((err) => {
        console.error("Failed to copy to clipboard:", err);
      });
  };

  const backgroundImage =
    activeTab === "narrative" ? narrativeBackground : desktopBackground;

  return (
    <header
      className={`header ${isTransitioning ? "header--transitioning" : ""}`}
      style={{ backgroundImage: `url(${backgroundImage})` }}
    >
      {activeTab === "commercial" && (
        <video
          className="header__video"
          autoPlay={true}
          muted={true}
          loop={true}
          playsInline={true}
          preload="auto"
        >
          <source src={websiteBg} type="video/mp4" />
        </video>
      )}
      <nav className="header__nav">
        <div className="header__cta">
          <button
            type="button"
            className={`header__btn-me ${bioActive ? "header__btn-me--active" : ""}`}
            onClick={(e) => {
              setBioActive(!bioActive);
              setShowBio(!showBio);
              e.currentTarget.blur();
            }}
          >
            {bioActive ? "×" : "About Me"}
          </button>
          <button
            type="button"
            className="header__btn"
            onClick={handleCvDownload}
          >
            CV.PDF
          </button>
          <div className="header__logo-wrapper">
            <img
              src={brandLogo}
              alt="Logo"
              className="header__logo"
              onMouseEnter={() => setShowBio(true)}
              onMouseLeave={() => {
                if (!bioActive) {
                  setTimeout(() => setShowBio(false), 300);
                }
              }}
              onFocus={() => setShowBio(true)}
              onBlur={() => {
                if (!bioActive) {
                  setShowBio(false);
                }
              }}
            />
            <div
              className="header__tabs"
              role="tablist"
              aria-label="Header tabs"
            >
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === "commercial"}
                className={
                  "header__tab header__tab--left " +
                  (activeTab === "commercial" ? "header__tab--active" : "")
                }
                onClick={() => handleTabChange("commercial")}
              >
                COMMERCIAL
              </button>

              <div className="header__tab-separator" aria-hidden="true" />

              <button
                type="button"
                role="tab"
                aria-selected={activeTab === "narrative"}
                className={
                  "header__tab header__tab--right " +
                  (activeTab === "narrative" ? "header__tab--active" : "")
                }
                onClick={() => handleTabChange("narrative")}
              >
                CORPORATE & NARRATIVE
              </button>
            </div>
          </div>
          <div
            className={`header__contact-wrap ${showContactDetails ? "header__contact-wrap--active" : ""} ${isClosing ? "header__contact-wrap--closing" : ""}`}
          >
            <button
              type="button"
              className="header__btn header__btn-contact"
              onClick={(e) => {
                if (showContactDetails) {
                  setIsClosing(true);
                  setTimeout(() => setIsClosing(false), 300);
                }
                setShowContactDetails(!showContactDetails);
                e.currentTarget.blur();
              }}
            >
              <span className="header__focus-out">S</span>TALK{" "}
              <span className="header__focus">TO</span> ME
            </button>

            <div className="header__icons">
              {socialLinks.map(({ name, url, icon, size }) => (
                <button
                  type="button"
                  key={name}
                  onClick={() => openInNewTab(url)}
                  className="header__icon"
                  aria-label={name}
                >
                  <IconWrapper
                    iconName={icon}
                    className="header__icon-svg"
                    width={size || 28}
                    height={size || 28}
                  />
                </button>
              ))}
            </div>

            <div
              className="header__contact-details"
              data-visible={showContactDetails}
            >
              <div
                className={`header__contact-item ${
                  copiedItem === "email" ? "copy-success" : ""
                }`}
              >
                elya@2l-lab.com
                <button
                  type="button"
                  onClick={() =>
                    handleCopyToClipboard("elya@2l-lab.com", "email")
                  }
                  className="header__contact-copy"
                  aria-label="Copy email address"
                >
                  <IconWrapper iconName="copy" className="header__icon-svg" />
                </button>
              </div>
              <div
                className={`header__contact-item ${
                  copiedItem === "phone" ? "copy-success" : ""
                }`}
              >
                +33 6 85 38 36 13
                <button
                  type="button"
                  onClick={() =>
                    handleCopyToClipboard("+33 6 85 38 36 13", "phone")
                  }
                  className="header__contact-copy"
                  aria-label="Copy phone number"
                >
                  <IconWrapper iconName="copy" className="header__icon-svg" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {activeTab === "narrative" && (
        <div className="header__services">
          {services.map((service) => (
            <button
              key={service.id}
              type="button"
              className="header__service-card"
              onClick={() => {
                setIsOpen(true);
                setVideoId(service.id);
              }}
              aria-label={`View ${service.title}`}
            >
              <img
                src={service.image}
                alt={service.title}
                className="header__service-image"
              />
              <div className="header__service-info">
                <h3 className="header__service-title">{service.title}</h3>
                <p className="header__service-description">
                  {service.description}
                </p>
              </div>
            </button>
          ))}
        </div>
      )}

      {activeTab === "commercial" && (
        <button
          type="button"
          className="header__play"
          onClick={() => {
            setVideoId("commercial");
            setIsOpen(true);
          }}
          aria-label="Open trailer"
        >
          <IconWrapper
            iconName="play"
            className="header__play-icon"
            width={100}
            height={100}
          />
          <span className="header__play-text">DEMO</span>
          <span className="header__play-subtext">mini-concepts pub</span>
        </button>
      )}

      <div
        className={`header__content ${showBio ? "header--bio-visible" : ""}`}
      >
        {/* TODO: Test error UI */}
        {cvError && <p className="header__error">{cvError}</p>}
        <div className="header__bio-wrap">
          {activeTab === "commercial" ? (
            <div className="bio">
              <BioCommercial />
              <BioHeadshot />
            </div>
          ) : (
            <div className="bio bio--narrative">
              <BioNarrative />
              <BioHeadshot />
            </div>
          )}
        </div>
        <div
          className={`header__headline ${showBio ? "header__headline--active" : ""}`}
        >
          <h1
            className="signature"
            onMouseEnter={() => setShowBio(true)}
            onMouseLeave={() => {
              if (!bioActive) {
                setTimeout(() => setShowBio(false), 300);
              }
            }}
            onFocus={() => setShowBio(true)}
            onBlur={() => {
              if (!bioActive) {
                setShowBio(false);
              }
            }}
          >
            <span className="header__headline-firstname">Elya</span>
            <br />
            <span className="header__headline-lastname">Randrianaivo</span>
            <br />
            <span className="header__headline-title">DIRECTOR</span>
          </h1>
        </div>
      </div>

      <VideoModal
        videoId={videoId} // Placeholder video ID for narrative tab
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
      />
    </header>
  );
};

export default Header;
export { services };
